import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { Wordmark } from "@/components/Logo";

const NAV_ITEMS = [
  { key: "projects", href: "#projects" },
  { key: "howItWorks", href: "#how-it-works" },
  { key: "benefits", href: "#bento" },
  { key: "transparency", href: "#transparency" },
  { key: "contact", href: "#contact" },
];

function LanguageSwitcher() {
  const { lang, t } = useI18n();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  const target = lang === "it" ? "en" : "it";
  const stripped = pathname.replace(/^\/(it|en)(?=\/|$)/, "") || "";
  const newPath = `/${target}${stripped}${hash || ""}`;

  const onSwitch = (e) => {
    e.preventDefault();
    // preserve scroll position by using replace-like nav
    navigate(newPath, { replace: false });
  };

  return (
    <a
      href={newPath}
      onClick={onSwitch}
      className="group inline-flex items-center gap-1.5 rounded-full border border-[#1C352D]/15 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#1C352D] hover:border-[#C59B27] hover:text-[#C59B27] transition-colors"
      data-testid="language-switcher"
      aria-label={t("lang.switchTo")}
    >
      <span className={lang === "it" ? "text-[#1C352D]" : "text-[#5C6B64]"}>IT</span>
      <span className="text-[#D4AF37]">/</span>
      <span className={lang === "en" ? "text-[#1C352D]" : "text-[#5C6B64]"}>EN</span>
    </a>
  );
}

export default function Header() {
  const { lang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname, location.hash]);

  const homeLink = `/${lang}`;
  const isHome = location.pathname === homeLink || location.pathname === `${homeLink}/`;

  const buildHref = (hash) => (isHome ? hash : `${homeLink}${hash}`);

  return (
    <header
      className={`ecominerali-header fixed top-0 inset-x-0 z-50 transition-[background,border,padding] duration-300 ${
        scrolled ? "bg-[#F8F7F4]/85 backdrop-blur-md border-b border-[#1C352D]/10 py-3" : "bg-transparent border-b border-transparent py-5"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center justify-between gap-6">
        <Link to={homeLink} className="shrink-0" data-testid="header-home-link" aria-label="Ecominerali">
          <Wordmark />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.key}
              href={buildHref(item.href)}
              className="text-[13px] tracking-wide text-[#1A2421] hover:text-[#C59B27] transition-colors font-medium"
              data-testid={`nav-${item.key}`}
            >
              {t(`nav.${item.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <LanguageSwitcher />

          <a
            href={buildHref("#contact")}
            className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-[#1C352D] px-4 py-2 text-[12px] font-medium tracking-wide text-[#F8F7F4] hover:bg-[#2D4A3E] hover:pl-5 transition-[background,padding] group"
            data-testid="header-cta"
          >
            {t("nav.cta")}
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:rotate-45" />
          </a>

          <button
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#1C352D]/15 text-[#1C352D] hover:bg-[#1C352D] hover:text-[#F8F7F4] transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("nav.close") : t("nav.menu")}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden overflow-hidden bg-[#F8F7F4]/95 backdrop-blur-md border-t border-[#1C352D]/10"
            data-testid="mobile-menu-panel"
          >
            <div className="mx-auto max-w-[1400px] px-5 py-6 flex flex-col divide-y divide-[#1C352D]/10">
              {NAV_ITEMS.map((item, i) => (
                <a
                  key={item.key}
                  href={buildHref(item.href)}
                  className="py-4 flex items-center justify-between text-[15px] font-serif text-[#1C352D] hover:text-[#C59B27]"
                  data-testid={`mobile-nav-${item.key}`}
                  onClick={() => setOpen(false)}
                >
                  <span>{t(`nav.${item.key}`)}</span>
                  <span className="font-mono text-[10px] text-[#C59B27]">0{i + 1}</span>
                </a>
              ))}
              <a
                href={buildHref("#contact")}
                className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1C352D] px-4 py-3 text-[13px] font-medium tracking-wide text-[#F8F7F4]"
                data-testid="mobile-cta"
                onClick={() => setOpen(false)}
              >
                {t("nav.cta")}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
